/**
 * useAppleIap — StoreKit 2 purchase lifecycle for native iOS.
 *
 * Responsibilities:
 *  - Load localized products from StoreKit (prices are NEVER hardcoded).
 *  - Run purchases and forward the signed transaction to the server for
 *    cryptographic verification (`validate-apple-receipt`).
 *  - Restore purchases and re-verify each restored transaction.
 *  - Listen for StoreKit transaction updates (renewals, refunds, revocations,
 *    billing retry recoveries, Ask-to-Buy approvals) and re-verify them.
 *
 * Entitlement is never derived on-device: after every verification we ask the
 * SubscriptionContext to revalidate against the database.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { isAppleIapEnabled } from "@/lib/iap/feature-flag";
import { APPLE_PRODUCT_IDS, type ApplePlanKey } from "@/lib/iap/apple-products";
import {
  MBLearnIap,
  isCapacitorIapPluginAvailable,
  isNativeIosShell,
  isStoreKitPlatform,
  probeStoreKitAvailability,
  type AppleProduct,
} from "@/lib/iap/storekit-bridge";
import type { AppleUiState } from "@/lib/iap/apple-ui-states";
import type { IapDiagnosticsSnapshot } from "@/lib/iap/diagnostics";

export interface UseAppleIapResult {
  /** True when StoreKit can actually be used on this device/build. */
  storeKitReady: boolean;
  products: AppleProduct[];
  loadingProducts: boolean;
  /** Product id currently being purchased, or null. */
  purchasingProductId: string | null;
  restoring: boolean;
  /** Last UI state to surface (bilingual copy resolved by the component). */
  uiState: AppleUiState | null;
  /** Non-localized diagnostic detail, safe to log. */
  errorDetail: string | null;
  /** True while native plugin registration is being checked. */
  probingStoreKit: boolean;
  /** Non-sensitive on-screen diagnostics (no tokens, JWS or user data). */
  diagnostics: IapDiagnosticsSnapshot;
  purchase: (planKey: ApplePlanKey) => Promise<void>;
  restore: () => Promise<void>;
  manageSubscription: () => Promise<void>;
  reloadProducts: () => Promise<void>;
  clearUiState: () => void;
}

const PRODUCT_ID_LIST = Object.values(APPLE_PRODUCT_IDS);

export function useAppleIap(): UseAppleIapResult {
  const { user } = useAuth();
  const { revalidate } = useSubscriptionContext();

  const flagOn = isAppleIapEnabled();
  const nativeIos = isNativeIosShell();

  // Optimistic on native iOS: `isPluginAvailable` can be false during early
  // boot. The async probe below is authoritative and downgrades if needed.
  const [pluginAvailable, setPluginAvailable] = useState<boolean>(
    () => flagOn && (isStoreKitPlatform() || isNativeIosShell()),
  );
  const [probingStoreKit, setProbingStoreKit] = useState(flagOn && nativeIos);
  const enabled = flagOn && nativeIos && pluginAvailable;

  const [products, setProducts] = useState<AppleProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [purchasingProductId, setPurchasingProductId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [uiState, setUiState] = useState<AppleUiState | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [getProductsStarted, setGetProductsStarted] = useState(false);
  const [getProductsResponseReceived, setGetProductsResponseReceived] = useState(false);
  const [nativeProbeResponded, setNativeProbeResponded] = useState(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Server-side verification. Returns true when Apple confirmed the purchase.
   * `silent` suppresses error UI (used by the background entitlement sync).
   */
  const verifyOnServer = useCallback(
    async (signedTransaction: string, silent = false): Promise<boolean> => {
      const { data, error } = await supabase.functions.invoke("validate-apple-receipt", {
        body: { signedTransaction },
      });

      if (error) {
        if (!silent) {
          setErrorDetail(error.message ?? "verify_invoke_failed");
          setUiState("verification_failed");
        }
        return false;
      }
      if (!data?.success) {
        if (!silent) {
          if (data?.error === "linked_to_another_account") {
            setUiState("linked_to_another_account");
          } else {
            setUiState("verification_failed");
          }
          setErrorDetail(data?.reason ?? data?.error ?? "verification_failed");
        }
        return false;
      }

      await revalidate();
      return true;
    },
    [revalidate],
  );

  // Authoritative availability probe (survives late bridge registration).
  useEffect(() => {
    if (!flagOn || !nativeIos) {
      console.info("🍎 [IAP] skip probe", { flagOn, nativeIos });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { available, detail } = await probeStoreKitAvailability();
      console.info("🍎 [IAP] storekit probe", { available, detail });
      if (!cancelled && mounted.current) {
        setPluginAvailable(available);
        setNativeProbeResponded(true);
        setErrorDetail(available ? null : detail);
        setProbingStoreKit(false);
        console.info("🍎 [IAP] final availability state assigned", {
          storeKitReady: flagOn && nativeIos && available,
          uiState: available ? "ready_to_load_products" : "storekit_bridge_unavailable",
          detail,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flagOn, nativeIos]);

  const reloadProducts = useCallback(async () => {
    if (!enabled) {
      console.info("🍎 [IAP] reloadProducts skipped — storekit not enabled", {
        flagOn,
        nativeIos,
        pluginAvailable,
      });
      return;
    }
    setLoadingProducts(true);
    setGetProductsStarted(true);
    setGetProductsResponseReceived(false);
    setUiState("loading_products");
    setErrorDetail(null);
    console.info("🍎 [IAP] getProducts →", PRODUCT_ID_LIST);
    try {
      const { products: fetched, diagnostic } = await MBLearnIap.getProducts({
        productIds: PRODUCT_ID_LIST,
      });
      // Safe diagnostics only: no receipts, transactions or secrets.
      console.info("🍎 [IAP] getProducts ←", {
        bundleId: diagnostic?.bundleId ?? "unknown",
        storefront: diagnostic?.storefront ?? "unknown",
        environment: diagnostic?.environment ?? "unknown",
        receiptKind: diagnostic?.receiptKind ?? "unknown",
        requested: PRODUCT_ID_LIST,
        requestedCount: PRODUCT_ID_LIST.length,
        idIssues: diagnostic?.idIssues ?? [],
        missingProductIds: diagnostic?.missingProductIds ?? [],
        count: fetched?.length ?? 0,
        ids: (fetched ?? []).map((p) => p.productId),
      });

      if (!mounted.current) return;
      setGetProductsResponseReceived(true);
      setProducts(Array.isArray(fetched) ? fetched : []);
      setUiState(fetched?.length ? null : "product_unavailable");
      console.info("🍎 [IAP] final product state assigned", {
        storeKitReady: true,
        uiState: fetched?.length ? null : "product_unavailable",
        productCount: fetched?.length ?? 0,
        detail: fetched?.length ? null : "storekit_returned_zero_products",
      });
      if (!fetched?.length) setErrorDetail("storekit_returned_zero_products");
    } catch (e) {
      const detail = e instanceof Error ? e.message : "get_products_failed";
      console.error("🍎 [IAP] getProducts FAILED", detail, e);
      if (!mounted.current) return;
      setGetProductsResponseReceived(true);
      setProducts([]);
      setErrorDetail(detail);
      setUiState("product_unavailable");
      console.info("🍎 [IAP] final product state assigned", {
        storeKitReady: true,
        uiState: "product_unavailable",
        productCount: 0,
        detail,
      });
    } finally {
      if (mounted.current) setLoadingProducts(false);
    }
  }, [enabled, flagOn, nativeIos, pluginAvailable]);

  // Initial product load.
  useEffect(() => {
    if (!enabled) return;
    void reloadProducts();
  }, [enabled, reloadProducts]);

  // StoreKit transaction updates: renewals, refunds, revocations, Ask-to-Buy.
  useEffect(() => {
    if (!enabled) return;
    let handle: { remove: () => Promise<void> } | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const listener = await MBLearnIap.addListener(
          "transactionUpdated",
          (event) => {
            if (event?.signedTransaction) void verifyOnServer(event.signedTransaction);
          },
        );
        if (cancelled) void listener.remove();
        else handle = listener;
      } catch {
        /* listener unavailable — server notifications still reconcile state */
      }
    })();

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [enabled, verifyOnServer]);

  const purchase = useCallback(
    async (planKey: ApplePlanKey) => {
      if (!enabled) return;
      const productId = APPLE_PRODUCT_IDS[planKey];
      setPurchasingProductId(productId);
      setUiState("purchasing");
      // checkout_started = StoreKit purchase sheet is being invoked.
      trackEvent("checkout_started", "ios_iap", { plan: planKey, provider: "apple_iap" });
      setErrorDetail(null);
      try {
        const result = await MBLearnIap.purchase({
          productId,
          // Binds the Apple transaction to this MBLearn account server-side.
          appAccountToken: user?.id,
        });

        if (result.result === "cancelled") {
          setUiState("cancelled");
          return;
        }
        if (result.result === "pending") {
          setUiState("pending");
          return;
        }
        if (result.result !== "success" || !result.signedTransaction) {
          setErrorDetail(result.message ?? "purchase_failed");
          setUiState("verification_failed");
          return;
        }

        const ok = await verifyOnServer(result.signedTransaction);
        if (ok) {
          // checkout_completed = Apple transaction verified server-side.
          trackEvent("checkout_completed", "ios_iap", { plan: planKey, provider: "apple_iap" });
          setUiState("purchase_success");
        }
      } catch (e) {
        setErrorDetail(e instanceof Error ? e.message : "purchase_failed");
        setUiState("verification_failed");
      } finally {
        if (mounted.current) setPurchasingProductId(null);
      }
    },
    [enabled, user?.id, verifyOnServer],
  );

  const restoreInFlight = useRef(false);

  const restore = useCallback(async () => {
    if (!enabled) return;
    // One AppStore.sync() per tap: re-entrant calls would re-prompt for the
    // Apple ID password while the first one is still running.
    if (restoreInFlight.current) return;
    restoreInFlight.current = true;
    setRestoring(true);
    setUiState("restoring");
    setErrorDetail(null);
    try {
      const { signedTransactions } = await MBLearnIap.restorePurchases();
      const list = Array.isArray(signedTransactions) ? signedTransactions : [];

      if (list.length === 0) {
        setUiState("product_unavailable");
        setErrorDetail("no_transactions_found");
        return;
      }

      let verified = 0;
      for (const signedTransaction of list) {
        // eslint-disable-next-line no-await-in-loop -- sequential by design
        if (await verifyOnServer(signedTransaction)) verified += 1;
      }

      setUiState(verified > 0 ? "restore_success" : "verification_failed");
    } catch (e) {
      setErrorDetail(e instanceof Error ? e.message : "restore_failed");
      setUiState("verification_failed");
    } finally {
      restoreInFlight.current = false;
      if (mounted.current) setRestoring(false);
    }
  }, [enabled, verifyOnServer]);

  /**
   * Silent reconciliation: reads the current StoreKit entitlements (no
   * AppStore.sync(), so it never prompts for the Apple ID password) and
   * re-verifies them server-side. Runs once when the surface mounts so an
   * already-purchased user lands on PRO without touching Restore.
   */
  const syncedRef = useRef(false);
  useEffect(() => {
    if (!enabled || syncedRef.current) return;
    syncedRef.current = true;
    void (async () => {
      try {
        const { signedTransactions } = await MBLearnIap.getCurrentEntitlements();
        const list = Array.isArray(signedTransactions) ? signedTransactions : [];
        console.info("🍎 [IAP] silent entitlement sync", { count: list.length });
        for (const signedTransaction of list) {
          // eslint-disable-next-line no-await-in-loop -- sequential by design
          await verifyOnServer(signedTransaction, true);
        }
      } catch (e) {
        console.info("🍎 [IAP] silent entitlement sync skipped", e);
      }
    })();
  }, [enabled, verifyOnServer]);

  const manageSubscription = useCallback(async () => {
    if (!enabled) return;
    try {
      // Apple's native management sheet — no Safari, no external URL.
      await MBLearnIap.showManageSubscriptions();
    } catch (e) {
      setErrorDetail(e instanceof Error ? e.message : "manage_failed");
    }
  }, [enabled]);

  const clearUiState = useCallback(() => {
    setUiState(null);
    setErrorDetail(null);
  }, []);

  const diagnostics: IapDiagnosticsSnapshot = {
    isNativeIOS: nativeIos,
    flagEnabled: flagOn,
    capacitorPluginAvailable: isCapacitorIapPluginAvailable(),
    pluginAvailable,
    nativeProbeResponded,
    probing: probingStoreKit,
    getProductsStarted,
    getProductsResponseReceived,
    requestedProductIds: PRODUCT_ID_LIST,
    returnedProductIds: products.map((product) => product.productId),
    productCount: products.length,
    errorDetail,
    uiState,
    storeKitReady: enabled,
  };

  return {
    storeKitReady: enabled,
    products,
    loadingProducts,
    purchasingProductId,
    restoring,
    uiState,
    errorDetail,
    probingStoreKit,
    diagnostics,
    purchase,
    restore,
    manageSubscription,
    reloadProducts,
    clearUiState,
  };
}

export default useAppleIap;
